import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const features = [
    {
      icon: '🚗',
      title: 'Wide Vehicle Selection',
      description: 'Choose from sedans, SUVs, luxury cars, and vans for your specific needs.'
    },
    {
      icon: '⏱️',
      title: 'Hour-Based Pricing',
      description: 'Pay only for the hours you need with transparent pricing including GST.'
    },
    {
      icon: '🏙️',
      title: 'All UK Cities',
      description: 'Service available across all major cities in the United Kingdom.'
    },
    {
      icon: '🔒',
      title: 'Secure Booking',
      description: 'Verified drivers and secure payment processing for peace of mind.'
    },
    {
      icon: '📱',
      title: 'Real-Time Updates',
      description: 'Get instant notifications about your booking status and driver details.'
    },
    {
      icon: '⭐',
      title: 'Rated Drivers',
      description: 'All our drivers are rated and verified for quality service.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Happy Customers' },
    { number: '61+', label: 'Verified Drivers' },
    { number: '10+', label: 'UK Cities' },
    { number: '24/7', label: 'Customer Support' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About Uttarakhand Cabs
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're revolutionizing driver rental services across the Uttarakhand with 
              our innovative hour-based booking system and extensive network of professional drivers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-blue-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Uttarakhand Cabs?
            </h2>
            <p className="text-gray-600 text-lg">
              We provide the best driver rental experience in the UK
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gray-50 p-6 rounded-xl shadow-lg text-center"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                To make driver rental services accessible, affordable, and reliable for 
                everyone across the Uttarakhand. We believe in connecting people with 
                professional drivers through a seamless digital experience.
              </p>
              <p className="text-gray-600 text-lg">
                Our platform ensures transparency in pricing, quality in service, and 
                convenience in booking - all while supporting local drivers and their communities.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-blue-600 text-white p-6 rounded-lg text-center">
                <div className="text-2xl font-bold mb-2">Quality</div>
                <div>Service</div>
              </div>
              <div className="bg-green-600 text-white p-6 rounded-lg text-center">
                <div className="text-2xl font-bold mb-2">Trust</div>
                <div>Built</div>
              </div>
              <div className="bg-purple-600 text-white p-6 rounded-lg text-center">
                <div className="text-2xl font-bold mb-2">24/7</div>
                <div>Support</div>
              </div>
              <div className="bg-orange-600 text-white p-6 rounded-lg text-center">
                <div className="text-2xl font-bold mb-2">Safe</div>
                <div>Rides</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;